// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
/**
 * charts.ts — hand-rolled SVG/HTML chart helpers. No chart library. Every data
 * mark carries a styled [data-tip] for the global hover tooltip. Pure layout
 * maths (histogram binning) is exported for the positional layout tests.
 */
import { escapeHtml } from '../utils/format';

// ── Colour ────────────────────────────────────────────────────────────────
/** Sequential green ramp (light → dark) for the choropleth. */
export const RAMP = ['#e7f3ea', '#c3e2cb', '#98cfa6', '#66b881', '#3f9e60', '#237a44'];

export function niceCeil(v: number): number {
  if (v <= 0) return 1;
  const mag = Math.pow(10, Math.floor(Math.log10(v)));
  const norm = v / mag;
  const step = norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 5 ? 5 : 10;
  return step * mag;
}

/** Pick a ramp colour for value v given 6 ascending stops. */
export function rampColour(v: number | null, stops: number[]): string {
  if (v == null || !Number.isFinite(v)) return '#eef1f4';
  for (let i = 0; i < stops.length; i++) if (v <= stops[i]) return RAMP[i];
  return RAMP[RAMP.length - 1];
}

function hex(n: number): string { return Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0'); }
function lerpHex(a: string, b: string, t: number): string {
  const pa = [1, 3, 5].map((i) => parseInt(a.slice(i, i + 2), 16));
  const pb = [1, 3, 5].map((i) => parseInt(b.slice(i, i + 2), 16));
  return `#${pa.map((x, i) => hex(x + (pb[i] - x) * t)).join('')}`;
}
/** Recovery-rate 0–100 → clay-red (low) → amber (mid) → green (high). */
export function rateColour(rate: number | null): string {
  if (rate == null || !Number.isFinite(rate)) return '#eef1f4';
  const r = Math.max(0, Math.min(100, rate)) / 100;
  return r < 0.5 ? lerpHex('#c0503f', '#e0902d', r / 0.5) : lerpHex('#e0902d', '#2f8f4e', (r - 0.5) / 0.5);
}

// ── Donut ────────────────────────────────────────────────────────────────
export interface Seg { value: number; colour: string; label: string; tip?: string }

export function donutSvg(segs: Seg[], opts: { size?: number; thickness?: number; centre?: string; centreSub?: string } = {}): string {
  const size = opts.size ?? 168;
  const th = opts.thickness ?? 26;
  const r = (size - th) / 2;
  const cx = size / 2, cy = size / 2;
  const circ = 2 * Math.PI * r;
  const totalRaw = segs.reduce((a, s) => a + s.value, 0);
  const totalNZ = totalRaw > 0 ? totalRaw : 1;
  let offset = 0;
  const arcs = segs
    .filter((s) => s.value > 0)
    .map((s) => {
      const frac = s.value / totalNZ;
      const dash = frac * circ;
      const seg = `<circle r="${r}" cx="${cx}" cy="${cy}" fill="none" stroke="${s.colour}" stroke-width="${th}"
        stroke-dasharray="${dash} ${circ - dash}" stroke-dashoffset="${-offset}"
        transform="rotate(-90 ${cx} ${cy})" data-tip="${escapeHtml(s.tip ?? `${s.label}: ${((frac) * 100).toFixed(1)}%`)}"
        aria-label="${escapeHtml(s.label)}" style="cursor:default"><title></title></circle>`;
      offset += dash;
      return seg;
    })
    .join('');
  const centre = opts.centre
    ? `<text x="${cx}" y="${cy - (opts.centreSub ? 4 : -2)}" text-anchor="middle" style="font-family:var(--font-mono);font-weight:800;font-size:${size / 7}px;fill:var(--text-primary)">${escapeHtml(opts.centre)}</text>` +
      (opts.centreSub ? `<text x="${cx}" y="${cy + 16}" text-anchor="middle" style="font-size:11px;fill:var(--text-tertiary)">${escapeHtml(opts.centreSub)}</text>` : '')
    : '';
  return `<svg class="chart donut" viewBox="0 0 ${size} ${size}" width="${size}" height="${size}" role="img">${arcs}${centre}</svg>`;
}

// ── 100% stacked horizontal bar (div) ──────────────────────────────────────
export function stackedBarHtml(segs: Seg[], opts: { tall?: boolean } = {}): string {
  const total = segs.reduce((a, s) => a + s.value, 0) || 1;
  return `<div class="mix-bar${opts.tall ? ' tall' : ''}">${segs
    .filter((s) => s.value > 0)
    .map((s) => {
      const pct = (s.value / total) * 100;
      return `<div class="mix-seg" style="width:${pct.toFixed(2)}%;background:${s.colour}" data-tip="${escapeHtml(s.tip ?? `${s.label}: ${pct.toFixed(1)}%`)}"></div>`;
    })
    .join('')}</div>`;
}

export function legendHtml(items: Array<{ colour: string; label: string }>, opts: { round?: boolean } = {}): string {
  return `<div class="legend">${items
    .map((i) => `<span class="legend-item" style="cursor:default"><span class="legend-swatch${opts.round ? ' round' : ''}" style="background:${i.colour}"></span>${escapeHtml(i.label)}</span>`)
    .join('')}</div>`;
}

// ── Ranked horizontal bars (SVG) ────────────────────────────────────────────
export interface RankedItem { label: string; value: number; colour: string; tip?: string; data?: Record<string, string>; secondary?: string }
export function rankedBarsSvg(items: RankedItem[], opts: { width?: number; unit?: string; valueFmt?: (v: number) => string; clickAttr?: string } = {}): string {
  const width = opts.width ?? 720;
  const rowH = 30;
  const labelW = Math.min(200, Math.max(96, Math.round(width * 0.26)));
  const pad = 8;
  const valueW = 66;
  const barMax = width - labelW - valueW - pad * 2;
  const max = Math.max(...items.map((i) => i.value), 1);
  const height = items.length * rowH + 6;
  const fmt = opts.valueFmt ?? ((v: number) => v.toLocaleString('en-AU'));
  const rows = items
    .map((it, i) => {
      const y = i * rowH + 4;
      const w = Math.max(1, (it.value / max) * barMax);
      const dataAttrs = it.data ? Object.entries(it.data).map(([k, v]) => `data-${k}="${escapeHtml(v)}"`).join(' ') : '';
      const click = opts.clickAttr && it.data ? ` role="button" style="cursor:pointer"` : ' style="cursor:default"';
      return `<g class="ranked-row" ${dataAttrs}${click} data-tip="${escapeHtml(it.tip ?? `${it.label}: ${fmt(it.value)}${opts.unit ? ' ' + opts.unit : ''}`)}">
        <rect x="0" y="${y - 4}" width="${width}" height="${rowH}" fill="transparent"></rect>
        <text x="0" y="${y + rowH / 2 - 4}" dominant-baseline="middle" style="font-size:12px;fill:var(--text-secondary)">${escapeHtml(clip(it.label, 24))}</text>
        <rect class="bar" x="${labelW}" y="${y}" width="${w}" height="${rowH - 12}" rx="3" fill="${it.colour}"></rect>
        <text x="${labelW + w + 6}" y="${y + rowH / 2 - 4}" dominant-baseline="middle" style="font-family:var(--font-mono);font-size:11px;fill:var(--text-secondary)">${escapeHtml(fmt(it.value))}${it.secondary ? '' : ''}</text>
      </g>`;
    })
    .join('');
  return `<div class="chart-scroll"><svg class="chart ranked" viewBox="0 0 ${width} ${height}" width="100%" height="${height}" preserveAspectRatio="xMinYMin meet" role="img">${rows}</svg></div>`;
}

function clip(s: string, n: number): string { return s.length > n ? s.slice(0, n - 1) + '…' : s; }

// ── Line chart (SVG), one or more series ────────────────────────────────────
export interface LineSeries { label: string; colour: string; points: Array<{ x: number; y: number | null }>; dashed?: boolean; width?: number }
export function lineChartSvg(
  series: LineSeries[],
  opts: { width?: number; height?: number; xLabels?: string[]; yMax?: number; yMin?: number; yFmt?: (v: number) => string; yTicks?: number; bands?: Array<{ x0: number; x1: number; label?: string; colour?: string }>; markers?: boolean } = {},
): string {
  const width = opts.width ?? 720;
  const height = opts.height ?? 300;
  const mL = 44, mR = 14, mT = 12, mB = 30;
  const plotW = width - mL - mR;
  const plotH = height - mT - mB;
  const allX = series.flatMap((s) => s.points.map((p) => p.x));
  const xMin = Math.min(...allX), xMax = Math.max(...allX);
  const allY = series.flatMap((s) => s.points.map((p) => p.y).filter((y): y is number => y != null));
  const yMax = opts.yMax ?? niceCeil(Math.max(...allY, 1));
  const yMin = opts.yMin ?? 0;
  const sx = (x: number) => mL + (xMax === xMin ? plotW / 2 : ((x - xMin) / (xMax - xMin)) * plotW);
  const sy = (y: number) => mT + plotH - ((y - yMin) / (yMax - yMin || 1)) * plotH;
  const yFmt = opts.yFmt ?? ((v: number) => String(v));
  const yTicks = opts.yTicks ?? 4;
  const grid = Array.from({ length: yTicks + 1 }, (_, i) => {
    const v = yMin + ((yMax - yMin) * i) / yTicks;
    const y = sy(v);
    return `<line class="grid-line" x1="${mL}" y1="${y}" x2="${width - mR}" y2="${y}"></line><text x="${mL - 6}" y="${y + 3}" text-anchor="end" style="font-size:10px;fill:var(--text-tertiary)">${escapeHtml(yFmt(v))}</text>`;
  }).join('');
  const bands = (opts.bands ?? [])
    .map((b) => `<rect x="${sx(b.x0)}" y="${mT}" width="${Math.max(0, sx(b.x1) - sx(b.x0))}" height="${plotH}" fill="${b.colour ?? 'rgba(192,80,63,0.08)'}"></rect>${b.label ? `<text x="${(sx(b.x0) + sx(b.x1)) / 2}" y="${mT + 12}" text-anchor="middle" style="font-size:10px;fill:var(--text-tertiary)">${escapeHtml(b.label)}</text>` : ''}`)
    .join('');
  const xlabels = (opts.xLabels ?? [])
    .map((lab, i, arr) => {
      if (arr.length > 8 && i % 2 !== 0 && i !== arr.length - 1) return '';
      const x = sx(xMin + i);
      return `<text x="${x}" y="${height - 8}" text-anchor="middle" style="font-size:10px;fill:var(--text-tertiary)">${escapeHtml(lab)}</text>`;
    })
    .join('');
  const paths = series
    .map((s) => {
      let d = '';
      let started = false;
      for (const p of s.points) {
        if (p.y == null) { started = false; continue; }
        d += `${started ? 'L' : 'M'}${sx(p.x).toFixed(1)} ${sy(p.y).toFixed(1)}`;
        started = true;
      }
      return `<path d="${d}" fill="none" stroke="${s.colour}" stroke-width="${s.width ?? 2.2}" stroke-linejoin="round" stroke-linecap="round"${s.dashed ? ' stroke-dasharray="5 4"' : ''}></path>`;
    })
    .join('');
  const dots = opts.markers
    ? series
        .map((s) => s.points.filter((p) => p.y != null).map((p) => `<circle cx="${sx(p.x).toFixed(1)}" cy="${sy(p.y as number).toFixed(1)}" r="2.6" fill="${s.colour}" data-tip="${escapeHtml(`${s.label}: ${yFmt(p.y as number)}`)}"></circle>`).join(''))
        .join('')
    : '';
  return `<div class="chart-scroll"><svg class="chart line" viewBox="0 0 ${width} ${height}" width="100%" height="${height}" preserveAspectRatio="xMinYMin meet" role="img">${bands}${grid}${paths}${dots}${xlabels}</svg></div>`;
}

// ── Stacked area (SVG) ──────────────────────────────────────────────────────
export interface AreaBand { key: string; label: string; colour: string; values: number[] }
export function stackedAreaSvg(bands: AreaBand[], xLabels: string[], opts: { width?: number; height?: number; yFmt?: (v: number) => string; percent?: boolean } = {}): string {
  const width = opts.width ?? 720;
  const height = opts.height ?? 320;
  const mL = 46, mR = 14, mT = 12, mB = 30;
  const plotW = width - mL - mR;
  const plotH = height - mT - mB;
  const n = xLabels.length;
  const totals = Array.from({ length: n }, (_, i) => bands.reduce((a, b) => a + (b.values[i] || 0), 0));
  const yMax = opts.percent ? 100 : niceCeil(Math.max(...totals, 1));
  const sx = (i: number) => mL + (n === 1 ? plotW / 2 : (i / (n - 1)) * plotW);
  const sy = (v: number) => mT + plotH - (v / yMax) * plotH;
  const norm = (i: number, v: number) => (opts.percent ? (totals[i] ? (v / totals[i]) * 100 : 0) : v);
  // cumulative
  const cum = Array.from({ length: n }, () => 0);
  const yFmt = opts.yFmt ?? ((v: number) => String(v));
  const grid = Array.from({ length: 5 }, (_, i) => {
    const v = (yMax * i) / 4;
    const y = sy(v);
    return `<line class="grid-line" x1="${mL}" y1="${y}" x2="${width - mR}" y2="${y}"></line><text x="${mL - 6}" y="${y + 3}" text-anchor="end" style="font-size:10px;fill:var(--text-tertiary)">${escapeHtml(opts.percent ? `${v.toFixed(0)}%` : yFmt(v))}</text>`;
  }).join('');
  const areas = bands
    .map((b) => {
      const top: string[] = [];
      const bottom: string[] = [];
      for (let i = 0; i < n; i++) {
        const v = norm(i, b.values[i] || 0);
        const y0 = cum[i];
        const y1 = cum[i] + v;
        top.push(`${sx(i).toFixed(1)} ${sy(y1).toFixed(1)}`);
        bottom.push(`${sx(i).toFixed(1)} ${sy(y0).toFixed(1)}`);
        cum[i] = y1;
      }
      const d = `M${top.join('L')}L${bottom.reverse().join('L')}Z`;
      return `<path d="${d}" fill="${b.colour}" fill-opacity="0.9" stroke="#fff" stroke-width="0.4" data-tip="${escapeHtml(b.label)}"></path>`;
    })
    .join('');
  const xlabels = xLabels
    .map((lab, i) => (n > 8 && i % 2 !== 0 && i !== n - 1 ? '' : `<text x="${sx(i)}" y="${height - 8}" text-anchor="middle" style="font-size:10px;fill:var(--text-tertiary)">${escapeHtml(lab)}</text>`))
    .join('');
  return `<div class="chart-scroll"><svg class="chart area" viewBox="0 0 ${width} ${height}" width="100%" height="${height}" preserveAspectRatio="xMinYMin meet" role="img">${grid}${areas}${xlabels}</svg></div>`;
}

// ── Histogram binning (pure — positional test target) ──────────────────────
export interface Bin { x0: number; x1: number; count: number; items: number[] }
export function histogramBins(values: number[], nBins: number, lo?: number, hi?: number): Bin[] {
  const vals = values.filter((v) => Number.isFinite(v));
  const min = lo ?? (vals.length ? Math.min(...vals) : 0);
  const max = hi ?? (vals.length ? Math.max(...vals) : 1);
  const span = max - min || 1;
  const n = Math.max(1, Math.floor(nBins));
  const bins: Bin[] = Array.from({ length: n }, (_, i) => ({
    x0: min + (span * i) / n,
    x1: min + (span * (i + 1)) / n,
    count: 0,
    items: [] as number[],
  }));
  for (let vi = 0; vi < vals.length; vi++) {
    const v = vals[vi];
    let idx = Math.floor(((v - min) / span) * n);
    if (idx < 0) idx = 0;
    if (idx >= n) idx = n - 1;
    bins[idx].count++;
    bins[idx].items.push(vi);
  }
  return bins;
}

// ── Sparkline ───────────────────────────────────────────────────────────────
export function sparklineSvg(values: Array<number | null>, opts: { width?: number; height?: number; colour?: string } = {}): string {
  const w = opts.width ?? 88;
  const h = opts.height ?? 24;
  const pts = values.filter((v): v is number => v != null);
  if (pts.length < 2) return `<svg width="${w}" height="${h}" class="spark"></svg>`;
  const max = Math.max(...pts), min = Math.min(...pts);
  const span = max - min || 1;
  const step = w / (values.length - 1);
  let d = '';
  let started = false;
  values.forEach((v, i) => {
    if (v == null) { started = false; return; }
    const x = i * step;
    const y = h - 2 - ((v - min) / span) * (h - 4);
    d += `${started ? 'L' : 'M'}${x.toFixed(1)} ${y.toFixed(1)}`;
    started = true;
  });
  return `<svg width="${w}" height="${h}" class="spark" viewBox="0 0 ${w} ${h}"><path d="${d}" fill="none" stroke="${opts.colour ?? 'var(--accent-primary)'}" stroke-width="1.5" stroke-linejoin="round"></path></svg>`;
}
